import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next';
import { FaCar, FaSitemap, FaEdit, FaTrash } from 'react-icons/fa';
import { HiOutlineRectangleGroup } from 'react-icons/hi2';
import { useDispatch } from 'react-redux';
import { vehicleGroupService } from '../../Services/vehicle.group.service';
import { vehicleService } from '../../Services/vehicle.service';
import { useSelected } from '../../contexts/SelectedContext';
import { Filters } from '../../components/Filters';
import SyncFusionDateTimePicker from '../Components/SyncFusionDateTimePicker/SyncFusionDateTimePicker';
import { maintenancesService } from '../../Services/maintenances.service';
import { odometersService } from '../../Services/odometers.service';
import { ToastComponent } from '../Components/ToastComponent';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page } from '@syncfusion/ej2-react-grids';
import { Accordion } from 'react-bootstrap';
import { paginationService } from '../../Services/pagination.service';
import posthog from '../../Services/posthog';
import { toLocalDateTimeString } from '../../Services/string.service';

export const SchedulingMaintenances = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch(); /** eslint-disable-next-line */
    dispatch({ type: "title", value: "Agendamento de itens" });

    // SelectedContext
    const { selectedValues } = useSelected();
    const selectedGroup = selectedValues.veiculos?.[0] || 0;
    const selectedVehicle = selectedValues.veiculosGroups?.[0] || 0;
    const selectedItem = selectedValues.itens?.[0] || 0;

    const [vehicleGroups, setVehicleGroups] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [items, setItems] = useState([]);
    const [schedules, setSchedules] = useState([]);

    const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
    const [, setFiltersLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [startDate, setStartDate] = useState(null);
    const [executionDate, setExecutionDate] = useState(null);
    const [startKm, setStartKm] = useState('');
    const [scheduleDays, setScheduleDays] = useState('');
    const [maintenanceCost, setMaintenanceCost] = useState('');
    const [odometerValue, setOdometerValue] = useState('');
    const [toastParams, setToastParams] = useState(JSON.stringify({ show: false }));
    const [activeAccordionKeys, setActiveAccordionKeys] = useState(["0"]);
    const [pageSize, setPageSize] = useState(10);
    const [dataLoading] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    // Refs para medir layout
    const gridAnchorRef = useRef(null);

    // Constantes de layout
    const ROW_HEIGHT = 30;
    const HEADER_PAGER_BUDGET = 100;
    const BOTTOM_MARGIN = 30;
    const MIN_PAGE = 5;
    const MAX_PAGE = 100;


    const fetchVehicleGroups = async () => {
        try {
            setFiltersLoading(true);
            const groups = await vehicleGroupService.fetchGroups();

            // Garante que sempre seja um array, mesmo quando a API retorna erro
            if (Array.isArray(groups)) {
                setVehicleGroups(groups);
                if (groups.length === 1) {
                    await fetchVehiclesByGroup(groups[0].vehicleGroupId);
                } else {
                    await fetchVehiclesByGroup(0);
                }
            } else {
                // Se retornou null, undefined ou objeto de erro, define como array vazio
                setVehicleGroups([]);
                if (groups?.error) {
                    console.log("Erro ao buscar grupos:", groups.message || groups.friendlyMessage);
                }
                await fetchVehiclesByGroup(0);
            }
        } catch (error) {
            console.log(error);
            setVehicleGroups([]);
        } finally {
            setFiltersLoading(false);
        }
    };

    const fetchVehiclesByGroup = async (groupId) => {
        setIsLoadingVehicles(true);
        try {
            const vehicles = await vehicleService.fetchVehicles(groupId);
            // Garante que sempre seja um array, mesmo quando a API retorna erro
            if (Array.isArray(vehicles)) {
                setVehicles(vehicles);
            } else {
                // Se retornou null, undefined ou objeto de erro, define como array vazio
                setVehicles([]);
                if (vehicles?.error) {
                    console.log("Erro ao buscar veículos:", vehicles.message || vehicles.friendlyMessage);
                }
            }
        } catch (error) {
            console.log(error);
            setVehicles([]);
        } finally {
            setIsLoadingVehicles(false);
        }
    };



    const listItens = async () => {
        setIsLoadingVehicles(true);
        try {
            const itens = await maintenancesService.fetchItemsMaintenace();
            // Garante que sempre seja um array, mesmo quando a API retorna erro
            if (Array.isArray(itens)) {
                setItems(itens);
            } else {
                // Se retornou null, undefined ou objeto de erro, define como array vazio
                setItems([]);
                if (itens?.error) {
                    console.log("Erro ao buscar veículos:", itens.message || itens.friendlyMessage);
                }
            }
        } catch (error) {
            console.log(error);
            setItems([]);
        } finally {
            setIsLoadingVehicles(false);
        }
    };

    const handleSave = async () => {

        if (!selectedVehicle || selectedVehicle === "TODOS" || selectedVehicle === 0) {
            setToastParams(
                JSON.stringify({
                    show: true,
                    title: "Erro",
                    body: "Selecione um veículo específico antes de salvar.",
                    color: "Danger"
                })
            );
            return;
        }

        if (!selectedItem) {
            setToastParams(
                JSON.stringify({
                    show: true,
                    title: "Erro",
                    body: "Selecione um item de manutenção antes de salvar.",
                    color: "Danger"
                })
            );
            return;
        }

        // Verificar se pelo menos um campo foi preenchido
        const hasSchedulingData = startDate || startKm || scheduleDays;
        const hasExecutionData = maintenanceCost && executionDate;

        if (!hasSchedulingData && !hasExecutionData) {
            setToastParams(
                JSON.stringify({
                    show: true,
                    title: "Erro",
                    body: "Por favor, preencha pelo menos um campo antes de salvar.",
                    color: "Danger"
                })
            );
            return;
        }

        // Regra de agendamento: data obrigatória + pelo menos km ou intervalo
        if (activeAccordionKeys.includes("0")) {
            if (!startDate) {
                setToastParams(JSON.stringify({ show: true, title: "Erro", body: "A data é obrigatória para agendamento.", color: "Danger" }));
                return;
            }
            if (!startKm && !scheduleDays) {
                setToastParams(JSON.stringify({ show: true, title: "Erro", body: "Preencha pelo menos o KM ou o Intervalo para agendamento.", color: "Danger" }));
                return;
            }
        }

        // Regra de lançamento avulso: custo e data obrigatórios
        if (activeAccordionKeys.includes("1") && !activeAccordionKeys.includes("0")) {
            if (!executionDate) {
                setToastParams(JSON.stringify({ show: true, title: "Erro", body: "A data é obrigatória para lançamento avulso.", color: "Danger" }));
                return;
            }
            if (!maintenanceCost) {
                setToastParams(JSON.stringify({ show: true, title: "Erro", body: "O custo é obrigatório para lançamento avulso.", color: "Danger" }));
                return;
            }
        }

        // Determinar o tipo de agendamento
        let maintenanceBase = '';
        let mileage = null;
        let days = 0;
        let maintenanceStatus = 1;

        if (startKm) {
            maintenanceBase = 'KM';
            mileage = parseInt(startKm.replace(/\./g, ''));
        } if (scheduleDays) {
            maintenanceBase = 'DI';
            days = parseInt(scheduleDays);
        }

        if (activeAccordionKeys.includes("1")) {
            maintenanceStatus = 4;
            if (executionDate && new Date(executionDate) > new Date()) {
                setToastParams(JSON.stringify({
                    show: true,
                    title: "Erro",
                    body: "A data do lançamento avulso não pode ser maior que hoje.",
                    color: "Danger"
                }));
                return;
            }
        } else if (startDate) {
            maintenanceStatus = new Date(startDate) > new Date() ? 1 : 3;
        }

        const schedulingData = {
            maintenanceScheduleId: isEditing ? editingItem.schedulingId : 0,
            vehicleId: selectedVehicle,
            maintenanceItemId: selectedItem,
            maintenanceBase,
            mileage,
            days,
            hours: 0,
            active: "S",
            cost: maintenanceCost ? parseFloat(maintenanceCost.replace(/[^\d,]/g, '').replace(',', '.')) : 0,
            maintenanceStatus,
            startDate: startDate ? toLocalDateTimeString(startDate) : (executionDate ? toLocalDateTimeString(executionDate) : null),
            executionDate: executionDate ? toLocalDateTimeString(executionDate) : null
        };

        setIsSaving(true);
        setToastParams(
            JSON.stringify({
                show: true,
                title: "Atenção",
                body: "Salvando agendamento...",
                color: "Warning"
            })
        );

        try {
            if (activeAccordionKeys.includes("1") && odometerValue) {
                try {
                    const normalizedOdometer = Number(odometerValue.replace(/\./g, ''));
                    await odometersService.createOdometer({
                        VehicleId: Number(selectedVehicle),
                        VehicleOdometerId: 0,
                        OdometerDateTime: toLocalDateTimeString(executionDate),
                        OdometerValue: normalizedOdometer,
                    });
                } catch (odometerError) {
                    console.error('Erro ao registrar hodômetro:', odometerError);
                    const msg = odometerError?.response?.data?.message || "Não foi possível registrar o hodômetro.";
                    setToastParams(
                        JSON.stringify({
                            show: true,
                            title: "Erro",
                            body: msg,
                            color: "Danger"
                        })
                    );
                    return;
                }
            }

            const result = await maintenancesService.createNewScheduling(schedulingData);
            if (result === true) {
                posthog.capture("maintenance scheduled", {
                    vehicle_id: selectedVehicle,
                    maintenance_item_id: selectedItem,
                    maintenance_base: maintenanceBase,
                    is_editing: isEditing,
                    schedule_type: activeAccordionKeys.includes("1") ? "avulso" : "agendamento",
                });

                setToastParams(
                    JSON.stringify({
                        show: true,
                        title: "Sucesso",
                        body: isEditing ? "Agendamento atualizado com sucesso!" : "Agendamento criado com sucesso!",
                        color: "Success"
                    })
                );
                handleClear();
                listSchedules(selectedVehicle);
            } else {
                setToastParams(
                    JSON.stringify({
                        show: true,
                        title: "Erro",
                        body: result?.message || (isEditing ? "Erro ao atualizar agendamento. Tente novamente." : "Erro ao criar agendamento. Tente novamente."),
                        color: "Danger"
                    })
                );
            }
        } catch (error) {
            console.error('Erro ao salvar:', error);
            setToastParams(
                JSON.stringify({
                    show: true,
                    title: "Erro",
                    body: isEditing ? "Erro ao atualizar agendamento. Tente novamente." : "Erro ao criar agendamento. Tente novamente.",
                    color: "Danger"
                })
            );
        } finally {
            setIsSaving(false);
        }
    };

    const listSchedules = async (vehicleId) => {
        setIsLoadingVehicles(true);
        try {
            const itens = await maintenancesService.fetchItemsScheduling(vehicleId, "S");
            // Garante que sempre seja um array, mesmo quando a API retorna erro
            if (Array.isArray(itens)) {
                setSchedules(itens);
            } else {
                // Se retornou null, undefined ou objeto de erro, define como array vazio
                setSchedules([]);
                if (itens?.error) {
                    console.log("Erro ao buscar veículos:", itens.message || itens.friendlyMessage);
                }
            }
        } catch (error) {
            console.log(error);
            setSchedules([]);
        } finally {
            setIsLoadingVehicles(false);
        }
    };


    const handleClear = () => {
        setStartDate(null);
        setExecutionDate(null);
        setStartKm('');
        setScheduleDays('');
        setMaintenanceCost('');
        setOdometerValue('');
        setEditingItem(null);
        setIsEditing(false);
    };

    const formatCurrency = (value) => {
        const numericValue = value.replace(/\D/g, '');
        const formattedValue = (numericValue / 100).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        });
        return formattedValue;
    };

    const handleCostChange = (e) => {
        const rawValue = e.target.value;
        const formattedValue = formatCurrency(rawValue);
        setMaintenanceCost(formattedValue);
    };

    const formatKm = (value) => {
        const numericValue = value.replace(/\D/g, '');
        return numericValue.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
    };

    const handleKmChange = (e) => {
        const rawValue = e.target.value;
        const formattedValue = formatKm(rawValue);
        setStartKm(formattedValue);
    };

    const handleOdometerChange = (e) => {
        const rawValue = e.target.value;
        const formattedValue = formatKm(rawValue);
        setOdometerValue(formattedValue);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR');
    };

    const handleEdit = (data) => {
        setEditingItem(data);
        setIsEditing(true);

        // Preencher os campos com os dados do item
        if (data.startDate) {
            setStartDate(new Date(data.startDate));
        }
        if (data.executionDate) {
            setExecutionDate(new Date(data.executionDate));
        }
        if (data.kmInterval) {
            setStartKm(formatKm(data.kmInterval.toString()));
        }
        if (data.daysInterval) {
            setScheduleDays(data.daysInterval.toString());
        }
        if (data.cost) {
            setMaintenanceCost(data.cost.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }));
        }

        // Abrir o accordion apropriado
        if (data.kmInterval || data.daysInterval || data.startDate) {
            setActiveAccordionKeys(["0"]);
        } else if (data.cost || data.executionDate) {
            setActiveAccordionKeys(["1"]);
        }
    };

    const handleDelete = async (data) => {

        const deleteData = {
            maintenanceScheduleId: data.schedulingId,
            vehicleId: data.vehicleId,
            maintenanceItemId: data.maintenanceItemId,
            maintenanceBase: data.maintenanceBase ?? "DI",
            mileage: data.kmInterval,
            days: data.daysInterval,
            hours: 0,
            active: "N",
            cost: data.cost || 0,
            maintenanceStatus: 1,
            startDate: data.startDate ? toLocalDateTimeString(new Date(data.startDate)) : null,
            executionDate: data.executionDate ? toLocalDateTimeString(new Date(data.executionDate)) : null
        };

        setIsSaving(true);
        try {
            const result = await maintenancesService.createNewScheduling(deleteData);
            if (result === true) {
                setToastParams(
                    JSON.stringify({
                        show: true,
                        title: "Sucesso",
                        body: "Agendamento excluído com sucesso!",
                        color: "Success"
                    })
                );
                listSchedules(selectedVehicle);
            } else {
                setToastParams(
                    JSON.stringify({
                        show: true,
                        title: "Erro",
                        body: result?.message || "Erro ao excluir agendamento. Tente novamente.",
                        color: "Danger"
                    })
                );
            }
        } catch (error) {
            console.error('Erro ao excluir:', error);
            setToastParams(
                JSON.stringify({
                    show: true,
                    title: "Erro",
                    body: "Erro ao excluir agendamento. Tente novamente.",
                    color: "Danger"
                })
            );
        } finally {
            setIsSaving(false);
        }
    };

    // Carrega veículos quando o grupo selecionado muda
    useEffect(() => {
        if (selectedGroup !== null && selectedGroup !== undefined) {
            fetchVehiclesByGroup(selectedGroup);
        }
    }, [selectedGroup]);

    // Carrega agendamentos quando o veículo selecionado muda
    useEffect(() => {
        if (selectedVehicle) {
            listSchedules(selectedVehicle);
        }
    }, [selectedVehicle]);

    useEffect(() => {
        fetchVehicleGroups();
        listItens()
        listSchedules(selectedVehicle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!dataLoading) paginationService.calculateGridMetrics(
            gridAnchorRef,
            BOTTOM_MARGIN,
            HEADER_PAGER_BUDGET,
            ROW_HEIGHT, MIN_PAGE,
            MAX_PAGE,
            schedules,
            window.innerHeight,
            setPageSize
        );

        let t = null;
        const onResize = () => {
            if (t) window.clearTimeout(t);
            t = window.setTimeout(() => paginationService.calculateGridMetrics(
                gridAnchorRef,
                BOTTOM_MARGIN,
                HEADER_PAGER_BUDGET,
                ROW_HEIGHT, MIN_PAGE,
                MAX_PAGE,
                schedules,
                window.innerHeight,
                setPageSize
            ), 50);
        };

        window.addEventListener("resize", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
            if (t) window.clearTimeout(t);
        };
    }, [dataLoading, schedules]);


    return (
        <div className="container-xxl flex-grow-1 container-p-y">
            <ToastComponent
                params={toastParams}
                closeToast={() => setToastParams(JSON.stringify({ show: false }))}
            />

            <p>Configure o item que deseja ter o controle de uso</p>
            <div className="filter-maintenance">
                <Filters
                    id={"veiculos"}
                    isMulti={false}
                    title={t("VIEW_MAP_FILTERS.GROUPS")}
                    hasSubTitle={true}
                    subTitle={t("VIEW_MAP_FILTERS.ALL_GROUPS")}
                    options={vehicleGroups || []}
                    icon={HiOutlineRectangleGroup}
                    hasIcon={true}
                    pageTitle={"maintenances_scheduling"}
                />
                <Filters
                    id={"veiculosGroups"}
                    isMulti={false}
                    title={t("VIEW_MAP_FILTERS.VEHICLES")}
                    hasSubTitle={true}
                    subTitle={t("VIEW_MAP_FILTERS.ALL_VEHICLES")}
                    options={vehicles || []}
                    icon={FaCar}
                    hasIcon={true}
                    pageTitle={"maintenances_scheduling"}
                    isLoading={isLoadingVehicles}
                />
                <Filters
                    id={"itens"}
                    isMulti={false}
                    title={"Itens"}
                    hasSubTitle={true}
                    subTitle={"Selecione um item"}
                    options={items || []}
                    icon={FaSitemap}
                    hasIcon={true}
                    pageTitle={"maintenances_scheduling"}
                    isLoading={isLoadingVehicles}
                />
            </div>

            <Accordion
                className="custom-accordion"
                defaultActiveKey={activeAccordionKeys}
                onSelect={(rawKey) => {
                    const keys = rawKey?.toString().split(",").filter(Boolean) || [];
                    setActiveAccordionKeys(keys);

                    // Limpa campos do accordion fechado
                    if (keys.includes("0") && !keys.includes("1")) {
                        // Accordion 0 aberto, limpa campos do accordion 1
                        setMaintenanceCost('');
                        setExecutionDate(null);
                        setOdometerValue('');
                    } else if (keys.includes("1") && !keys.includes("0")) {
                        // Accordion 1 aberto, limpa campos do accordion 0
                        setStartDate(null);
                        setStartKm('');
                        setScheduleDays('');
                    }
                }}
            >
                <Accordion.Item eventKey="0">
                    <Accordion.Header>
                        <span>Agendamento</span>
                    </Accordion.Header>
                    <Accordion.Body>
                        <div className="container-maintenances">
                            <div className="field">
                                <SyncFusionDateTimePicker
                                    id="onlyDate"
                                    value={startDate}
                                    onChange={setStartDate}
                                    placeholder={"Data"}
                                    step={30}
                                />
                            </div>

                            <div className="field">
                                <span>Km:</span>
                                <input
                                    className='form-control'
                                    type="text"
                                    name="startKm"
                                    value={startKm}
                                    onChange={handleKmChange}
                                    placeholder="0"
                                    style={{ textAlign: 'left' }}
                                />
                            </div>

                            <div className="field">
                                <label>Intervalo:</label>
                                <select
                                    name="scheduleType"
                                    className='form-control'
                                    value={scheduleDays}
                                    onChange={(e) => setScheduleDays(e.target.value)}
                                >
                                    <option value="">Selecione</option>
                                    <option value="0">0 dias</option>
                                    <option value="30">30 dias</option>
                                    <option value="60">60 dias</option>
                                    <option value="90">90 dias</option>
                                    <option value="180">180 dias</option>
                                    <option value="365">365 dias</option>
                                </select>
                            </div>
                        </div>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                    <Accordion.Header>
                        <span>Lançamento avulso</span>
                    </Accordion.Header>
                    <Accordion.Body>
                        <div className="container-maintenances">
                            <div className="field">
                                <label>Custo da manutenção:</label>
                                <input
                                    className='form-control'
                                    type="text"
                                    name="maintenanceCost"
                                    value={maintenanceCost}
                                    onChange={handleCostChange}
                                    placeholder="R$ 0,00"
                                    style={{ textAlign: 'left' }}
                                />
                            </div>

                            <div className="field">
                                <SyncFusionDateTimePicker
                                    id="onlyDate"
                                    value={executionDate}
                                    onChange={setExecutionDate}
                                    placeholder={"Data"}
                                    step={30}
                                />
                            </div>

                            <div className="field">
                                <label>Hodômetro:</label>
                                <input
                                    className='form-control'
                                    type="text"
                                    name="odometerValue"
                                    value={odometerValue}
                                    onChange={handleOdometerChange}
                                    placeholder="0"
                                    style={{ textAlign: 'left' }}
                                />
                            </div>
                        </div>

                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            <div className="actions-buttons">
                <button
                    type="button"
                    className="btn-primary"
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Salvar')}
                </button>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleClear}
                    disabled={isSaving}
                >
                    Limpar
                </button>
            </div>

            <div className="grid-section mt-4">
                {/* Âncora para medir posição do grid na viewport */}
                <div ref={gridAnchorRef} />

                {schedules.length > 0 ? (
                    <GridComponent
                        dataSource={schedules}
                        allowPaging={true}
                        pageSettings={{ pageSize }}
                        rowHeight={ROW_HEIGHT}
                        height={"auto"}
                    >
                        <ColumnsDirective>
                            <ColumnDirective field='plate' headerText='Veiculo' width='150' />
                            <ColumnDirective
                                field='maintenanceItemDescription'
                                headerText='Item'
                                width='150'
                            />
                            <ColumnDirective field='kmInterval' headerText='KM' width='150' />
                            <ColumnDirective
                                field="daysInterval"
                                headerText="Tempo (dias)"
                                width="200"
                                template={(props) => {
                                    const value = props.daysInterval;

                                    return value === 0 || value === " " || value === "" || value == null
                                        ? null
                                        : value;
                                }}
                            />
                            <ColumnDirective
                                field='startDate'
                                headerText='Adicionado'
                                width='150'
                                template={(props) => formatDate(props.startDate)}
                            />
                            <ColumnDirective
                                headerText='Ações'
                                width='120'
                                template={(props) => (
                                    <div style={{ display: 'flex', gap: '16px' }}>
                                        <FaEdit
                                            style={{ cursor: 'pointer', color: 'var(--text-primary)', fontSize: '12px' }}
                                            onClick={() => handleEdit(props)}
                                        />
                                        <FaTrash
                                            data-delete-action="true"
                                            aria-label="Excluir"
                                            style={{ cursor: 'pointer', color: 'var(--text-primary)', fontSize: '12px' }}
                                            onClick={() => handleDelete(props)}
                                        />
                                    </div>
                                )}
                            />
                        </ColumnsDirective>
                        <Inject services={[Page]} />
                    </GridComponent>
                ) : (
                    <span>Não há itens agendados para este veículo</span>
                )}
            </div>

        </div>
    )
}
